import { FilterTemplateParameter } from '@core/models/filter-preset.model';
import { GbeErrorCodes } from '@core/models/gbe/gbe-error-codes.enum';
import { GbeError } from '@core/models/gbe/gbe-error.model';
import { isoToDatetimeLocal } from '@core/utils/recipe-parameter-schema.utils';
import { isNil } from '@core/utils/shared-utils';

import {
  HoleIssueCode,
  HoleKind,
  distinctHoles,
  holeName,
  scanHoles,
  substituteHoles,
  withoutUnfilledHoles
} from './placeholders';
import { FilterField, FilterVocabulary } from './vocabulary/vocabulary';
import { FilterExpr, edgePath, opArgument } from './wire';

export const TEMPLATE_MAX_PARAMETERS = 50;
export const TEMPLATE_LABEL_MAX_LENGTH = 80;
export const TEMPLATE_NAME_MAX_LENGTH = 64;

/** The parameters of a template being authored: one per hole of its tree, labelled as typed. */
export function templateParameters(
  expr: FilterExpr | null | undefined,
  labels: Readonly<Record<string, string>>
): FilterTemplateParameter[] {
  return (expr ? distinctHoles(expr) : []).map(({ name, kind }) => ({
    name,
    kind,
    label: (labels[name] ?? '').trim()
  }));
}

export type TemplateIssueCode =
  | HoleIssueCode
  | 'no-parameters'
  | 'too-many-parameters'
  | 'undeclared'
  | 'unused'
  | 'missing-label'
  | 'label-too-long'
  | 'name-too-long';

export interface TemplateIssue {
  code: TemplateIssueCode;
  name?: string;
}

/**
 * Everything gbe would refuse on save, plus the slot rules gbe leaves to the client (it matches
 * hole names only). Labels are judged trimmed, as they are sent trimmed.
 */
export function templateIssues(
  expr: FilterExpr | null | undefined,
  parameters: readonly FilterTemplateParameter[]
): TemplateIssue[] {
  const scan = expr ? scanHoles(expr) : { holes: [], issues: [] };
  const issues: TemplateIssue[] = scan.issues.map((issue) =>
    issue.name === undefined ? { code: issue.code } : { code: issue.code, name: issue.name }
  );
  if (parameters.length === 0) issues.push({ code: 'no-parameters' });
  if (parameters.length > TEMPLATE_MAX_PARAMETERS)
    issues.push({ code: 'too-many-parameters' });

  const holes = new Set(scan.holes.map((hole) => hole.name));
  const declared = new Set(parameters.map((parameter) => parameter.name));
  for (const name of holes) {
    if (!declared.has(name)) issues.push({ code: 'undeclared', name });
  }
  for (const { name, label } of parameters) {
    if (!holes.has(name)) issues.push({ code: 'unused', name });
    if (name.length > TEMPLATE_NAME_MAX_LENGTH)
      issues.push({ code: 'name-too-long', name });
    const trimmed = label.trim();
    if (trimmed.length === 0) issues.push({ code: 'missing-label', name });
    else if (trimmed.length > TEMPLATE_LABEL_MAX_LENGTH)
      issues.push({ code: 'label-too-long', name });
  }
  return issues;
}

export interface TemplateMismatch {
  undeclared: string[];
  unused: string[];
}

function names(value: unknown): string[] | null {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
    ? value
    : null;
}

/** The hole/parameter mismatch gbe answers with `data_is_invalid`; `null` for any other failure. */
export function templateMismatchOf(error: unknown): TemplateMismatch | null {
  const gbeError = error as Partial<GbeError> | null | undefined;
  if (gbeError?.errorCode !== GbeErrorCodes.INVALID_DATA_CODE) return null;
  const details = gbeError.details as
    | { undeclared?: unknown; unused?: unknown }
    | undefined;
  const undeclared = names(details?.undeclared);
  const unused = names(details?.unused);
  return undeclared && unused ? { undeclared, unused } : null;
}

export type TemplateValues = Record<string, string | string[]>;

export interface TemplateInput {
  name: string;
  label: string;
  kind: HoleKind;
  /** The entity a uid hole refers to; `null` for any other hole, or a field the vocabulary lost. */
  uidEntityType: string | null;
  /** The closed values a hole on an enum field takes, with their served titles; `null` otherwise. */
  closed: Pick<FilterField, 'enumValues' | 'enumLabels'> | null;
}

/** Holes standing for a `uid` literal or for the items of a `uid_list`, anywhere in an argument. */
function uidHoleNames(value: unknown): string[] {
  if (Array.isArray(value)) return value.flatMap(uidHoleNames);
  if (value === null || typeof value !== 'object') return [];
  const record = value as Record<string, unknown>;
  const found: string[] = [];
  const uid = record['uid'];
  if (typeof uid === 'string') {
    const name = holeName(uid);
    if (name) found.push(name);
  }
  const uidList = record['uid_list'];
  if (Array.isArray(uidList)) {
    for (const item of uidList) {
      const name = typeof item === 'string' ? holeName(item) : null;
      if (name) found.push(name);
    }
  }
  for (const [key, inner] of Object.entries(record)) {
    if (key !== 'uid' && key !== 'uid_list') found.push(...uidHoleNames(inner));
  }
  return found;
}

interface HoleFields {
  /** The entity each uid hole refers to. */
  uids: Map<string, string>;
  /** The enum field each hole on a closed-valued field filters. */
  closed: Map<string, FilterField>;
}

/** What the fields a template's holes sit in say about the values those holes take; first use wins. */
function holeFields(expr: FilterExpr, vocabulary: FilterVocabulary): HoleFields {
  const found: HoleFields = { uids: new Map(), closed: new Map() };
  const visit = (node: FilterExpr, edge: string | null): void => {
    if ('and' in node) node.and.forEach((child) => visit(child, edge));
    else if ('or' in node) node.or.forEach((child) => visit(child, edge));
    else if ('not' in node) visit(node.not, edge);
    else if ('association' in node)
      visit(node.association.filter, edgePath(edge, node.association.edge));
    else {
      const field = vocabulary.field(edge, node.predicate.path);
      if (field?.uidEntityType) {
        for (const name of uidHoleNames(opArgument(node.predicate.op))) {
          if (!found.uids.has(name)) found.uids.set(name, field.uidEntityType);
        }
      } else if (field?.enumValues?.length) {
        for (const { name } of scanHoles(node).holes) {
          if (!found.closed.has(name)) found.closed.set(name, field);
        }
      }
    }
  };
  visit(expr, null);
  return found;
}

export function templateInputs(
  expr: FilterExpr | null | undefined,
  parameters: readonly FilterTemplateParameter[],
  vocabulary: FilterVocabulary | null
): TemplateInput[] {
  const fields: HoleFields =
    templateIsUsable(expr) && vocabulary
      ? holeFields(expr as FilterExpr, vocabulary)
      : { uids: new Map(), closed: new Map() };
  return parameters.map(({ name, label, kind }) => {
    const closed = fields.closed.get(name);
    return {
      name,
      label,
      kind,
      uidEntityType: fields.uids.get(name) ?? null,
      closed: closed
        ? { enumValues: closed.enumValues, enumLabels: closed.enumLabels }
        : null
    };
  });
}

/**
 * A template whose holes all sit in value slots. gbe matches hole names only and checks no schema, so
 * a template stored by another client may hold a hole in a label key or a field name, or be no filter
 * at all; none of those can be filled.
 */
export function templateIsUsable(expr: FilterExpr | null | undefined): boolean {
  if (isNil(expr)) return false;
  try {
    return scanHoles(expr).issues.length === 0;
  } catch (error) {
    if (error instanceof TypeError) return false;
    throw error;
  }
}

/**
 * The concrete tree, or `null` while any parameter is blank or the template is not usable: a hole
 * must never be sent as a value.
 */
export function fillTemplate(
  expr: FilterExpr | null | undefined,
  parameters: readonly FilterTemplateParameter[],
  values: TemplateValues
): FilterExpr | null {
  if (isNil(expr) || !templateIsUsable(expr)) return null;
  const filled: TemplateValues = {};
  for (const { name } of parameters) {
    const value = values[name];
    const usable = Array.isArray(value)
      ? value.map((item) => item.trim()).filter((item) => item.length > 0)
      : (value ?? '').trim();
    if (usable.length === 0) return null;
    filled[name] = usable;
  }
  const concrete = substituteHoles(expr, filled);
  // A value typed as `{{x}}` would be a hole again.
  const left = scanHoles(concrete);
  return left.holes.length === 0 && left.issues.length === 0 ? concrete : null;
}

export function parseListInput(raw: string): string[] {
  return raw
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

/** A value as the template pill shows it; the wire's UTC instant would read as the wrong hour. */
export function templateValueText(
  kind: HoleKind,
  value: string | string[] | undefined
): string {
  if (Array.isArray(value)) return value.join(', ');
  if (isNil(value)) return '';
  return kind === 'date-time' ? isoToDatetimeLocal(value).replace('T', ' ') : value;
}

/**
 * Any hole, well-formed or not: a write must not guess what a broken one meant. A tree the scan cannot
 * walk (gbe stores template trees unchecked) counts as holding one, for the same reason.
 */
export function holdsHoles(expr: FilterExpr | null | undefined): boolean {
  if (!expr) return false;
  try {
    const scan = scanHoles(expr);
    return scan.holes.length > 0 || scan.issues.length > 0;
  } catch (error) {
    if (error instanceof TypeError) return true;
    throw error;
  }
}

/**
 * The tree without its template holes, as a filter the user could have written: a related-entity
 * block a hole emptied goes too, where kept it would read as "has a related row".
 */
export function withoutTemplateHoles(expr: FilterExpr): FilterExpr | undefined {
  return withoutUnfilledHoles(expr, new Set(), 'drop');
}

/**
 * What a filter bar queries with while its tree holds holes: a hole sent as a value would be compared
 * as literal text, so its condition is left out. A tree the hole scan cannot walk (gbe stores template
 * trees unchecked) goes as it is, so the data layer's refusal reaches the filter panel.
 */
export function templatePreviewTree(tree: FilterExpr): FilterExpr | undefined {
  try {
    return withoutUnfilledHoles(tree, new Set());
  } catch (error) {
    if (error instanceof TypeError) return tree;
    throw error;
  }
}
